import { tsoPqTheme } from '@assets/style/customTsoPqtheme';
import ButtonTsoPq from '@components/Buttons/ButtonTsoPq';
import { zodResolver } from '@hookform/resolvers/zod';
import { Box, styled } from '@mui/material';
import { Dispatch, ReactNode, SetStateAction, useEffect } from 'react';
import { FormProvider, SubmitHandler, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import z, { TypeOf } from 'zod';

type FormTsoPqProps = {
  children: ({
    register,
    watch,
    setValue,
    errors,
    trigger,
    t,
    customProps,
    setError,
  }: {
    register: any;
    watch: any;
    setValue: any;
    errors: any;
    trigger: any;
    t: (key: string) => string;
    customProps: any;
    setError: any;
  }) => ReactNode;
  defaultValues?: Record<string, any>;
  onSubmit: (data: any, setError: any) => void;
  discardChanges?: Dispatch<SetStateAction<boolean>>;
  validationRules?: Record<string, any>;
  customProps?: any;
  newForm?: boolean;
  stickyPosition?: boolean;
  values?: Record<string, any>;
  customSubmitLabel?: string;
  customDiscardLabel?: string;
  withoutDiscard?: boolean;
  schema?: z.ZodTypeAny;
  hideSaveButton?: boolean;
};

interface StyledProps {
  stickyPosition?: boolean;
}
const StyledButtonWrapper = styled(Box)<StyledProps>(({ stickyPosition }) => ({
  display: 'flex',
  justifyContent: 'flex-end',
  gap: tsoPqTheme.spacingValues.pageSpacing.spacing_page_gap_page_gap_xxs,
  padding: `${tsoPqTheme.spacingValues.pageSpacing.spacing_page_gap_page_gap_s} 0 0 0`,
  ...(stickyPosition && {
    position: 'sticky',
    bottom: '0',
    zIndex: '99',
    backgroundColor: tsoPqTheme.palette.background.other.color_other_background_white,
  }),
}));

const FormTsoPq = ({
  children,
  onSubmit,
  defaultValues = {},
  validationRules = {},
  discardChanges,
  customProps,
  newForm,
  stickyPosition,
  values,
  customSubmitLabel,
  customDiscardLabel,
  withoutDiscard = false,
  schema,
  hideSaveButton,
  ...props
}: FormTsoPqProps) => {
  const inputSchema = schema ?? z.object({});
  type RegisterInput = TypeOf<typeof inputSchema>;
  const { t } = useTranslation();
  const methods = useForm<RegisterInput>({
    defaultValues,
    values,
    resolver: zodResolver(inputSchema, {
      errorMap: (issue, ctx) => {
        if (
          issue.code === 'invalid_type' &&
          issue.received === 'undefined' &&
          ctx.defaultError === 'Required'
        ) {
          return { message: t('general.error.requiredField') };
        }
        return { message: ctx.defaultError };
      },
    }),
  });

  const submitLabel = customSubmitLabel ?? t('management.general.save');
  const discardLabel = customDiscardLabel ?? t('management.general.discard');

  const { reset } = useForm({
    defaultValues,
  });

  useEffect(() => {
    if (defaultValues) {
      reset(defaultValues);
    }
  }, [defaultValues, reset]);

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit((data) => onSubmit(data, methods.setError))}>
        {children({
          register: methods.register,
          watch: methods.watch,
          setValue: methods.setValue,
          errors: methods.formState.errors,
          trigger: methods.trigger,
          t: t,
          customProps,
          setError: methods.setError,
        })}
        <StyledButtonWrapper stickyPosition={stickyPosition}>
          {!withoutDiscard ? (
            <ButtonTsoPq
              onClick={() => {
                methods.reset();
                discardChanges && discardChanges(false);
              }}
              sizeVariant="m"
              buttonType="outlinedPrimary"
              textContent={newForm ? t('management.general.cancel') : discardLabel}
            ></ButtonTsoPq>
          ) : null}
          {!hideSaveButton && (
            <ButtonTsoPq
              sizeVariant="m"
              type="submit"
              buttonType="filledPrimary"
              textContent={newForm ? t('management.general.create') : submitLabel}
            ></ButtonTsoPq>
          )}
        </StyledButtonWrapper>
      </form>
    </FormProvider>
  );
};

export default FormTsoPq;
